import { getFirestore, collection, query, where, getDocs, onSnapshot, orderBy } from "firebase/firestore";
import { app } from "../firebase-config.js";

const db = getFirestore(app);

class EventServiceImpl {
    constructor() {
        this.activeEvents = [];
        this.subscribers = new Set();
        this.initSubscription();
    }

    initSubscription() {
        // Subscribe to all 'active' events.
        // We filter by date client-side to ensure real-time accuracy without complex composite indexes for now.
        const q = query(collection(db, "events"), where("isActive", "==", true));

        onSnapshot(q, (snapshot) => {
            this.activeEvents = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            this.notifySubscribers();
        });
    }

    subscribe(callback) {
        this.subscribers.add(callback);
        callback(this.activeEvents); // Initial data
        return () => this.subscribers.delete(callback);
    }

    notifySubscribers() {
        this.subscribers.forEach(cb => cb(this.activeEvents));
    }

    /**
     * Returns the currently active 'GOLDEN_TIME' event if valid.
     */
    /**
     * Returns ALL currently active 'GOLDEN_TIME' events.
     */
    getActiveGoldenTimeEvents() {
        const now = new Date();
        return this.activeEvents.filter(e => {
            if (e.type !== 'GOLDEN_TIME') return false;

            // Simplified & Safe Parsing (Regression Fix)
            let start = e.startDate ? (e.startDate.toDate ? e.startDate.toDate() : new Date(e.startDate)) : new Date(0);
            let end = e.endDate ? (e.endDate.toDate ? e.endDate.toDate() : new Date(e.endDate)) : new Date(0);

            // Validation
            if (isNaN(start.getTime()) || isNaN(end.getTime())) return false;

            return now >= start && now <= end;
        });
    }

    /**
     * Returns the single (first) currently active 'GOLDEN_TIME' event.
     * Kept for backward compatibility but prefers getActiveGoldenTimeEvents.
     */
    getCurrentGoldenTimeEvent() {
        const events = this.getActiveGoldenTimeEvents();
        return events.length > 0 ? events[0] : undefined;
    }

    /**
     * Get multiplier for a specific series being viewed.
     * Returns the MAXIMUM multiplier if multiple events target the same series.
     */
    getBoostMultiplier(seriesId) {
        const events = this.getActiveGoldenTimeEvents();
        const matchingEvents = events.filter(e => e.seriesId === seriesId);

        if (matchingEvents.length === 0) return 1;

        // Return max multiplier
        return Math.max(...matchingEvents.map(e => e.multiplier || 1));
    }

    /**
     * Get active plaza banner text
     * Returns the text of the most recently started active event.
     */
    getActiveBanner() {
        const events = this.getActiveGoldenTimeEvents();
        if (events.length > 0) {
            // Sort by start date desc to show newest event? Or create a rotation?
            // For now, return the last one (newest potentially)
            return events[events.length - 1].bannerText;
        }
        return null;
    }

    /**
     * Helper: Get Theme Colors based on Multiplier
     */
    getEventTheme(multiplier) {
        if (multiplier >= 5) {
            return {
                badgeBg: 'bg-pink-600',
                borderRing: 'ring-pink-500',
                toastGradient: 'from-pink-500 to-rose-600',
                toastBorder: 'border-pink-200',
                toastText: 'text-pink-100'
            };
        } else if (multiplier >= 3) {
            return {
                badgeBg: 'bg-blue-600', // Facebook Blue-ish
                borderRing: 'ring-blue-500',
                toastGradient: 'from-blue-500 to-indigo-600',
                toastBorder: 'border-blue-200',
                toastText: 'text-blue-100'
            };
        } else {
            return {
                badgeBg: 'bg-yellow-400', // Gold
                borderRing: 'ring-yellow-400',
                toastGradient: 'from-yellow-400 to-orange-500',
                toastBorder: 'border-yellow-200',
                toastText: 'text-yellow-100' // or yellow-800 for contrast? yellow-100 on gradient is fine
            };
        }
    }
}

export const EventService = new EventServiceImpl();
